/**
 * ConfirmationDialog
 * Accessible confirmation dialog with focus trap + focus restoration.
 *
 * Used for high-stakes user decisions (leave protection, last attempt, cancel flows).
 */

import React, { useCallback, useEffect, useId, useRef } from 'react';
import { AlertTriangle, Info, X } from 'lucide-react';

type ConfirmationTone = 'danger' | 'warning' | 'info';

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface ConfirmationDialogProps {
  readonly isOpen: boolean;
  readonly tone?: ConfirmationTone;
  readonly title: string;
  readonly description: string;
  readonly confirmLabel: string;
  readonly cancelLabel: string;
  readonly onConfirm: () => void | Promise<void>;
  readonly onClose: () => void;
  readonly isLoading?: boolean;
  readonly disableBackdropClose?: boolean;
}

function getToneStyles(tone: ConfirmationTone): {
  icon: React.ReactNode;
  iconBg: string;
  confirmButton: string;
} {
  if (tone === 'danger') {
    return {
      icon: <AlertTriangle className="h-6 w-6 text-red-600" />,
      iconBg: 'bg-red-100',
      confirmButton: 'bg-red-600 hover:bg-red-700'
    };
  }

  if (tone === 'warning') {
    return {
      icon: <AlertTriangle className="h-6 w-6 text-amber-700" />,
      iconBg: 'bg-amber-100',
      confirmButton: 'bg-amber-600 hover:bg-amber-700'
    };
  }

  return {
    icon: <Info className="h-6 w-6 text-emerald-700" />,
    iconBg: 'bg-emerald-100',
    confirmButton: 'bg-emerald-600 hover:bg-emerald-700'
  };
}

export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  tone = 'warning',
  title,
  description,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onClose,
  isLoading = false,
  disableBackdropClose = false
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const uid = useId();
  const titleId = `confirmation-title-${uid}`;
  const descriptionId = `confirmation-description-${uid}`;

  const { icon, iconBg, confirmButton } = getToneStyles(tone);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape' && !isLoading) {
        onClose();
        return;
      }

      if (e.key !== 'Tab') return;

      const modal = modalRef.current;
      if (!modal) return;

      const focusableElements =
        modal.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusableElements.length === 0) return;

      const first = focusableElements[0];
      const last = focusableElements[focusableElements.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [isLoading, onClose]
  );

  useEffect((): void | (() => void) => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      const timer = window.setTimeout(() => {
        const firstFocusable =
          modalRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
        firstFocusable?.focus();
      }, 50);
      return (): void => {
        window.clearTimeout(timer);
      };
    }

    if (previousFocusRef.current) {
      previousFocusRef.current.focus();
      previousFocusRef.current = null;
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={disableBackdropClose || isLoading ? undefined : onClose}
        role="presentation"
      />

      <div
        ref={modalRef}
        className="relative mx-4 w-full max-w-md rounded-xl bg-white p-6 shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        onKeyDown={handleKeyDown}
      >
        <button
          type="button"
          onClick={onClose}
          disabled={isLoading}
          className="absolute right-4 top-4 rounded-lg p-1 text-gray-400 transition-colors hover:text-gray-600 disabled:opacity-50"
          aria-label="Close dialog"
        >
          <X className="h-5 w-5" />
        </button>

        <div
          className={`mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full ${iconBg}`}
        >
          {icon}
        </div>

        <h2
          id={titleId}
          className="mb-2 text-center text-lg font-semibold text-gray-900"
        >
          {title}
        </h2>
        <p
          id={descriptionId}
          className="mb-6 whitespace-pre-line text-center text-sm text-gray-600"
        >
          {description}
        </p>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            className={`flex-1 rounded-lg px-4 py-2.5 text-sm font-medium text-white transition-colors disabled:opacity-50 ${confirmButton}`}
          >
            {isLoading ? 'Working...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationDialog;
